// ================================================================
// BabyOdin 活动账号体系类型定义
// 版本: 1.0
// 创建日期: 2025-09-02
// ================================================================

/**
 * 活动账号类型枚举
 */
export enum ActivityAccountType {
	/** 活动账号 - 可以创建和管理自己的活动 */
	ACTIVITY = "ACTIVITY",
	/** 超级账号 - 可以创建管理所有活动，并可以管理活动账号 */
	SUPER = "SUPER",
}

/**
 * 活动账号权限信息
 */
export interface ActivityAccountPermission {
	/** 权限记录ID */
	id: string;
	/** 用户Principal ID */
	userPrincipal: string;
	/** 账号类型 */
	accountType: ActivityAccountType;
	/** 授权者Principal ID */
	grantedBy: string;
	/** 授权原因或备注 */
	grantedReason?: string;
	/** 授权时间 */
	grantedAt: Date;
	/** 是否有效 */
	isActive: boolean;
	/** 创建时间 */
	createdAt: Date;
	/** 更新时间 */
	updatedAt: Date;
}

/**
 * 活动账号操作日志
 */
export interface ActivityAccountOperationLog {
	/** 日志记录ID */
	id: string;
	/** 被操作用户的Principal ID */
	targetUserPrincipal: string;
	/** 操作者Principal ID */
	operatorPrincipal: string;
	/** 操作类型 */
	operation: "GRANT" | "REVOKE" | "UPDATE";
	/** 账号类型 */
	accountType: ActivityAccountType;
	/** 操作原因 */
	reason?: string;
	/** 操作详细信息 */
	details?: Record<string, any>;
	/** 操作时间 */
	operationTime: Date;
}

/**
 * 活动账号权限检查结果
 */
export interface ActivityAccountStatus {
	/** 是否有活动账号权限（ACTIVITY或SUPER） */
	hasActivityAccount: boolean;
	/** 是否有超级账号权限 */
	hasSuperAccount: boolean;
	/** 具体的账号类型 */
	accountType: ActivityAccountType | "NONE";
	/** 授权者（如果有权限） */
	grantedBy?: string;
	/** 授权时间（如果有权限） */
	grantedAt?: Date;
	/** 授权原因（如果有权限） */
	grantedReason?: string;
}

/**
 * 创建活动账号请求
 */
export interface CreateActivityAccountRequest {
	/** 用户Principal ID */
	userPrincipal: string;
	/** 账号类型 */
	accountType: ActivityAccountType;
	/** 授权原因 */
	reason?: string;
}

/**
 * 活动账号API响应
 */
export interface ActivityAccountApiResponse<T = any> {
	/** 操作是否成功 */
	success: boolean;
	/** 错误信息（如果失败） */
	error?: string;
	/** 响应数据（如果成功） */
	data?: T;
}

/**
 * 活动账号列表响应
 */
export interface ActivityAccountListResponse
	extends ActivityAccountApiResponse {
	/** 活动账号列表 */
	accounts?: ActivityAccountPermission[];
	/** 总数 */
	total?: number;
}

/**
 * 活动账号详情响应
 */
export interface ActivityAccountDetailResponse
	extends ActivityAccountApiResponse {
	/** 权限信息 */
	permission?: {
		account_type: string;
		granted_by: string;
		granted_at: string;
		granted_reason?: string;
	} | null;
}

/**
 * 活动账号管理界面的表格行数据
 */
export interface ActivityAccountTableRow {
	/** 用户Principal ID */
	userPrincipal: string;
	/** 显示的用户名（前8位） */
	displayName: string;
	/** 账号类型显示文本 */
	accountTypeText: string;
	/** 账号类型 */
	accountType: ActivityAccountType;
	/** 授权者显示文本 */
	grantedByText: string;
	/** 授权时间显示文本 */
	grantedAtText: string;
	/** 授权原因 */
	grantedReason?: string;
	/** 状态显示文本 */
	statusText: string;
	/** 是否有效 */
	isActive: boolean;
}

/**
 * 活动功能权限映射
 */
export type ActivityFeature =
	| "create-activity" // 创建活动
	| "manage-own-activities" // 管理自己的活动
	| "manage-all-activities" // 管理所有活动
	| "manage-activity-accounts"; // 管理活动账号

/**
 * 活动功能权限配置
 */
export const ACTIVITY_FEATURE_PERMISSIONS: Record<
	ActivityFeature,
	ActivityAccountType[]
> = {
	"create-activity": [ActivityAccountType.ACTIVITY, ActivityAccountType.SUPER],
	"manage-own-activities": [
		ActivityAccountType.ACTIVITY,
		ActivityAccountType.SUPER,
	],
	"manage-all-activities": [ActivityAccountType.SUPER],
	"manage-activity-accounts": [ActivityAccountType.SUPER],
};

/**
 * 检查是否有指定功能的权限
 * @param feature 功能名称
 * @param accountType 用户的账号类型
 * @returns 是否有权限
 */
export function hasFeaturePermission(
	feature: ActivityFeature,
	accountType: ActivityAccountType | "NONE",
): boolean {
	if (accountType === "NONE") return false;

	const requiredTypes = ACTIVITY_FEATURE_PERMISSIONS[feature];
	return requiredTypes.includes(accountType as ActivityAccountType);
}

/**
 * 格式化用户Principal显示
 * @param principal 用户Principal ID
 * @param length 显示长度，默认8
 * @returns 格式化后的显示文本
 */
export function formatPrincipalDisplay(
	principal: string,
	length: number = 8,
): string {
	if (!principal) return "";
	if (principal.length <= length + 3) return principal;
	return `${principal.slice(0, length)}...`;
}

/**
 * 获取账号类型显示文本
 * @param accountType 账号类型
 * @param language 语言
 * @returns 显示文本
 */
export function getAccountTypeText(
	accountType: ActivityAccountType | "NONE",
	language: "zh" | "en" = "zh",
): string {
	const texts = {
		zh: {
			[ActivityAccountType.ACTIVITY]: "活动账号",
			[ActivityAccountType.SUPER]: "超级账号",
			NONE: "无权限",
		},
		en: {
			[ActivityAccountType.ACTIVITY]: "Activity Account",
			[ActivityAccountType.SUPER]: "Super Account",
			NONE: "No Permission",
		},
	};

	return texts[language][accountType] || texts[language]["NONE"];
}

/**
 * 获取操作类型显示文本
 * @param operation 操作类型
 * @param language 语言
 * @returns 显示文本
 */
export function getOperationText(
	operation: "GRANT" | "REVOKE" | "UPDATE",
	language: "zh" | "en" = "zh",
): string {
	const texts = {
		zh: {
			GRANT: "授权",
			REVOKE: "撤销",
			UPDATE: "更新",
		},
		en: {
			GRANT: "Grant",
			REVOKE: "Revoke",
			UPDATE: "Update",
		},
	};

	return texts[language][operation];
}
